import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { ReviewType } from '../../../propTypes';
import { duckify } from 'utils/duckify';
import { flashMessages, reviews } from '../../../ducks';

/**
 * TextArea editor for a review comment. Edits are debounced and saved to the server automatically.
 *
 * @param review the review object
 * @param className class name to add to the TextArea
 * @param commentRef comment identifier
 * @param placeholder placeholder text for TextArea
 * @param wait debounce wait time in milliseconds
 * @param showFlash function to display flash message
 * @param updateComment function to save comment
 * @return {JSX.Element}
 */
const ReviewCommentEditor = ({
  review,
  className,
  commentRef,
  placeholder = 'Click here to add your comments - they’ll save automatically.',
  wait = 500,

  // mapped props
  showFlash,
  updateComment
}) => {
  const [comment, setComment] = useState('');
  const [commenting, setCommenting] = useState(false);
  const invariant = [review.id, commentRef.field, commentRef.checklistItemId];

  useEffect(() => setComment(reviews.getComment(review, commentRef) || ''), invariant);

  const debounceUpdate = useCallback(
    debounce(
      (text) => updateComment(review, text, commentRef)
        .then(() => setCommenting(false))
        .catch(() => showFlash({ type: 'danger', message: 'An error occurred while saving comment.' })),
      wait
    ),
    invariant
  );

  const onChange = (comment) => {
    setComment(comment);
    setCommenting(true);
    debounceUpdate(comment);
  };

  return (
    <textarea
      className={classNames(
        className, 'review-comment-editor', 'form-control', 'form-control-sm', { 'dirty': commenting }
      )}
      name="review-comment"
      value={comment}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
};

ReviewCommentEditor.propTypes = {
  review: ReviewType.isRequired,
  commentRef: PropTypes.shape({
    field: PropTypes.string,
    checklistItemId: PropTypes.string,
  }).isRequired,
  wait: PropTypes.number,
  placeholder: PropTypes.string,
};

const mapDispatchToProps = (dispatch) => ({
  showFlash: (props) => dispatch(flashMessages.actions.show(props)),
  updateComment: (review, text, commentRef) => dispatch(reviews.actions.updateComment(review, text, commentRef))
});

export default duckify(() => ({}), () => ({}), mapDispatchToProps)(ReviewCommentEditor);
